import { displayUnitSymbol, type PricingUnitKey } from '../../constants/units'
import type { Unit } from '../../enums'

/**
 * The long-form name of a unit something is priced in, for a picker where a
 * bare `m²` would read as noise next to `Stk.` and `Pauschal`.
 *
 * Only the pricing units have one. Everywhere else a unit renders as its
 * symbol from `UNITS` — `W/(m·K)` is what an engineer reads and writes, and a
 * German rendering of it would name something the field does not contain.
 *
 * The symbol never appears here. It lives in `shared/constants/units.ts`, once.
 */
export const PRICING_UNIT_LABELS = {
  PIECE: 'Stück',
  SQUARE_METER: 'Quadratmeter',
  CUBIC_METER: 'Kubikmeter',
  METER: 'Meter',
  CENTIMETER: 'Zentimeter',
  LITER: 'Liter',
  KILOGRAM: 'Kilogramm',
  GRAM: 'Gramm',
  HOUR: 'Stunde',
  DAY: 'Tagessatz',
  FLAT: 'Pauschal',
} as const satisfies Record<PricingUnitKey, string>

/**
 * A unit written out, for a place that needs a word rather than a mark — a
 * datasheet row, where a blank reads as a unit nobody set.
 *
 * The symbol wherever there is one, because a symbol is not German. `FLAT` is
 * the single member without one, and its word is the table above.
 */
export function displayUnitName(unit: Unit | null | undefined): string | null {
  if (!unit) return null
  return displayUnitSymbol(unit) || PRICING_UNIT_LABELS.FLAT
}
